import type { Database } from '~~/types/supabase'
import type { MailDocument } from '~~/supabase/functions/_shared/mail/blocks'
import { STARTER_TEMPLATES, type Starter } from '~~/supabase/functions/_shared/mail/defaults'

export type { MailDocument, Block, BlockType, L, LText, Brand } from '~~/supabase/functions/_shared/mail/blocks'

type Public = Database['public']['Tables']
export type MailSettings = Public['mail_settings']['Row']
export type MailTemplate = Public['mail_templates']['Row']
export type MailFlow = Public['mail_flows']['Row']
export type MailCampaign = Public['mail_campaigns']['Row']
export type MailContact = Public['mail_contacts']['Row']
export type MailSend = Public['mail_sends']['Row']
export type MailThread = Public['mail_threads']['Row']
export type MailMessage = Public['mail_messages']['Row']

// Languages a mail can be written in; the organization's own list decides which are on.
export const MAIL_LANGUAGES = [
  { code: 'nl', label: 'Nederlands' },
  { code: 'en', label: 'English' },
  { code: 'de', label: 'Deutsch' },
  { code: 'fr', label: 'Français' },
] as const

const DEFAULT_SETTINGS = {
  mode: 'classic',
  consent_checkbox: false,
  consent_label: null,
  sender_name: null,
  brand: {},
  postal_address: null,
  attach_pdf: false,
} as const

/**
 * Tickable Mail for the current organization (docs/tickable-mail.md).
 * Settings without a row are the defaults; saving creates the row.
 */
export function useMail() {
  const supabase = useSupabaseClient<Database>()
  const userStore = useUserStore()
  const organizationId = computed(() => userStore.organization?.id as string | undefined)

  const settings = useState<MailSettings | null>('mail-settings', () => null)
  const loadingSettings = ref(false)

  const loadSettings = async () => {
    if (!organizationId.value) return null
    loadingSettings.value = true
    const { data } = await supabase.from('mail_settings').select('*')
      .eq('organization_id', organizationId.value).maybeSingle()
    settings.value = (data ?? { ...DEFAULT_SETTINGS, organization_id: organizationId.value }) as MailSettings
    loadingSettings.value = false
    return settings.value
  }

  const saveSettings = async (patch: Partial<MailSettings>) => {
    if (!organizationId.value) return
    const { data, error } = await supabase.from('mail_settings')
      .upsert({ organization_id: organizationId.value, ...patch } as never, { onConflict: 'organization_id' })
      .select('*').single()
    if (error) throw error
    settings.value = data as MailSettings
    return settings.value
  }

  /** Calls the mail-actions edge function; throws with the function's error code. */
  const action = async <T = unknown>(name: string, body: Record<string, unknown> = {}): Promise<T> => {
    const { data, error } = await supabase.functions.invoke('mail-actions', {
      body: { action: name, organization_id: organizationId.value, ...body },
    })
    if (error) {
      let code = error.message
      try {
        const payload = await (error as { context?: Response }).context?.json()
        code = payload?.error ?? code
      } catch { /* not json */ }
      throw new Error(code)
    }
    return data as T
  }

  const preview = (document: MailDocument, opts: { lang?: string; kind?: string; order_id?: string | null; event_id?: string | null } = {}) =>
    action<{ subject: string; preheader: string; html: string }>('preview', { document, ...opts })

  const isOn = computed(() => settings.value?.mode === 'tickable_mail')

  /** Switches the organization to Tickable Mail (the overview and Meer instellingen); throws on failure. */
  const switchOn = async () => {
    await action('switch_mode', { mode: 'tickable_mail' })
    await loadSettings()
  }
  const languages = computed<string[]>(() => ((userStore.organization as { languages?: string[] } | null)?.languages ?? ['nl']))

  /**
   * A campaign's own mail from a starter (mail_templates.campaign_id): the design lists never show
   * it, and the copy it replaces goes, like mail_campaign_use_template does.
   */
  const campaignMailFromStarter = async (campaign: { id: string; name: string; kind: string }, starter: Starter) => {
    const { data, error } = await supabase.from('mail_templates').insert({
      organization_id: organizationId.value!,
      name: campaign.name,
      kind: campaign.kind === 'service' ? 'transactional' : 'marketing',
      document: structuredClone(starter.document) as never,
      campaign_id: campaign.id,
      updated_by: userStore.user?.id ?? null,
    }).select('*').single()
    if (error || !data) throw error ?? new Error('mail_template_not_created')
    await supabase.from('mail_campaigns').update({ template_id: data.id }).eq('id', campaign.id)
    await supabase.from('mail_templates').delete().eq('campaign_id', campaign.id).neq('id', data.id)
    return data as MailTemplate
  }

  /**
   * Opens a draft campaign, new or reused. A newsletter starts on the "Nieuwsbrief" starter and
   * reuses a draft nobody touched yet (no group, its mail as the starter made it), so clicking twice
   * does not stack drafts; a message to the visitors of an activity reuses that activity's draft,
   * else starts one on the "Wijziging" starter.
   */
  const { t } = useI18n()
  const toast = useToast()
  const { date } = useLocaleFormat()
  const creatingCampaign = ref(false)
  // An invitation ("Codes mailen" on a discount) reuses that discount's draft, else starts one on the "Uitnodiging" starter.
  // reuseDraft is what a newsletter always does now; the overview still passes it.
  const createCampaign = async (opts: { kind?: 'news' | 'service' | 'invitation'; eventId?: string; reuseDraft?: boolean; discount?: { id: string; name: string } } = {}) => {
    if (!organizationId.value) return
    const kind = opts.kind ?? 'news'
    const starterOf = (key: string) => STARTER_TEMPLATES.find(s => s.key === key)
    creatingCampaign.value = true
    try {
      if (kind === 'news') {
        const { data: drafts } = await supabase.from('mail_campaigns').select('id, name, kind, template_id')
          .eq('organization_id', organizationId.value).eq('status', 'draft').eq('kind', 'news').eq('segment', '{}' as never)
          .order('updated_at', { ascending: false }).limit(10)
        const tplIds = (drafts ?? []).map(d => d.template_id).filter((x): x is string => !!x)
        const { data: tpls } = tplIds.length
          ? await supabase.from('mail_templates').select('id, created_at, updated_at').in('id', tplIds)
          : { data: [] }
        // untouched: the template was never saved after it was made (touch_updated_at moves updated_at)
        const draft = (drafts ?? []).find(d => !d.template_id || tpls?.some(x => x.id === d.template_id && x.created_at === x.updated_at))
        if (draft) {
          const news = starterOf('newsletter')
          if (!draft.template_id && news) await campaignMailFromStarter(draft, news).catch(() => null)
          return navigateTo(`/mail/campaigns/${draft.id}`)
        }
      } else {
        let q = supabase.from('mail_campaigns').select('id').eq('organization_id', organizationId.value)
          .eq('status', 'draft').eq('kind', kind)
        q = kind === 'service' ? q.contains('segment', { event_ids: [opts.eventId] })
          : q.eq('discount_id', opts.discount!.id).is('parent_campaign_id', null)
        const { data: draft } = await q.order('updated_at', { ascending: false }).limit(1).maybeSingle()
        if (draft) return navigateTo(kind === 'service' ? `/events/${opts.eventId}/message/${draft.id}?step=recipients` : `/mail/campaigns/${draft.id}`)
      }
      let name = t('mail_campaign_default_name', { date: date(new Date(), { day: 'numeric', month: 'long' }) })
      if (kind === 'service') {
        const { data: ev } = await supabase.from('events').select('title').eq('id', opts.eventId!).single()
        name = t('mail_service_default_name', { event: ev?.title ?? '' })
      } else if (kind === 'invitation') {
        name = t('mail_invitation_default_name', { discount: opts.discount!.name })
      }
      const { data, error } = await supabase.from('mail_campaigns')
        .insert({
          organization_id: organizationId.value, name, kind, created_by: userStore.user?.id ?? null,
          discount_id: kind === 'invitation' ? opts.discount!.id : null,
          segment: (kind === 'service' ? { event_ids: [opts.eventId], upcoming_only: true } : {}) as never,
        })
        .select('id, name, kind').single()
      if (error || !data) return showSupabaseError(toast, error, { summary: t('mail_campaign_create_failed'), life: 6000 })
      // ponytail: without the starter the wizard still opens; the content step offers it again
      const starter = starterOf(kind === 'service' ? 'change' : kind === 'invitation' ? 'invitation' : 'newsletter')
      if (starter) await campaignMailFromStarter(data, starter).catch(() => null)
      if (kind === 'service') return navigateTo(`/events/${opts.eventId}/message/${data.id}?step=recipients`)
      return navigateTo(`/mail/campaigns/${data.id}`)
    } finally {
      creatingCampaign.value = false
    }
  }

  /** A copy is a new draft with the same people and its own copy of the mail; opens it. */
  const copying = ref<string | null>(null)
  const copyCampaign = async (c: MailCampaign) => {
    if (!organizationId.value) return
    copying.value = c.id
    const { data, error } = await supabase.from('mail_campaigns').insert({
      organization_id: organizationId.value, kind: c.kind, segment: c.segment, discount_id: c.discount_id,
      name: t('mail_campaign_copy_name', { name: c.name }), created_by: userStore.user?.id ?? null,
    }).select('id').single()
    if (!error && data && c.template_id) {
      const copied = await supabase.rpc('mail_campaign_use_template', { p_campaign: data.id, p_template: c.template_id })
      if (copied.error) showSupabaseError(toast, copied.error, { summary: t('mail_campaign_template_failed'), life: 6000 })
    }
    copying.value = null
    if (error || !data) return showSupabaseError(toast, error, { summary: t('mail_campaign_create_failed'), life: 6000 })
    return navigateTo(`/mail/campaigns/${data.id}`)
  }

  /** Everyone in the organization a test may go to (owner and team), lower case, the caller first.
   *  The admin account is in every organization and is never offered, unless it is the caller. */
  const teamEmails = useState<string[] | null>('mail-team-emails', () => null)
  const loadTeamEmails = async () => {
    if (teamEmails.value || !organizationId.value) return teamEmails.value ?? []
    const [{ data: org }, { data: members }] = await Promise.all([
      supabase.from('organizations').select('owner_id').eq('id', organizationId.value).single(),
      supabase.from('organization_users').select('user_id').eq('organization_id', organizationId.value),
    ])
    const ids = [org?.owner_id, ...(members ?? []).map(m => m.user_id)].filter((id): id is string => !!id)
    const { data: users } = ids.length ? await supabase.from('users').select('email').in('id', ids) : { data: [] }
    const me = String(userStore.user?.email ?? '').toLowerCase()
    const all = [...new Set((users ?? []).map(u => String(u.email ?? '').trim().toLowerCase()).filter(Boolean))]
    teamEmails.value = [me, ...all.filter(e => e !== me && e !== 'henricopops@gmail.com')].filter(Boolean)
    return teamEmails.value
  }

  return { organizationId, settings, loadingSettings, loadSettings, saveSettings, action, preview, isOn, switchOn, languages, createCampaign, creatingCampaign, copyCampaign, copying, campaignMailFromStarter, loadTeamEmails }
}
